/**
 * Utility functions for sorting table data
 */

type TableRow = Record<string, unknown>;

/**
 * Generic comparator function for sorting strings
 * Handles null/undefined values by treating them as empty strings
 *
 * @param key - The key of the property to sort by
 * @returns A comparator function compatible with Array.prototype.sort()
 *
 * @example
 * ```ts
 * const headers = [{
 *   key: 'name',
 *   label: 'Name',
 *   sortFn: sortByString('name')
 * }]
 * ```
 */
export const sortByString =
  (key: string) =>
  (a: TableRow, b: TableRow): number => {
    const valueA = (a[key] as string) || '';
    const valueB = (b[key] as string) || '';
    return valueA.localeCompare(valueB);
  };

/**
 * Generic comparator function for sorting numbers
 * Handles null/undefined values by treating them as 0
 *
 * @param key - The key of the property to sort by
 * @returns A comparator function compatible with Array.prototype.sort()
 *
 * @example
 * ```ts
 * const headers = [{
 *   key: 'age',
 *   label: 'Age',
 *   sortFn: sortByNumber('age')
 * }]
 * ```
 */
export const sortByNumber =
  (key: string) =>
  (a: TableRow, b: TableRow): number => {
    const valueA = (a[key] as number) || 0;
    const valueB = (b[key] as number) || 0;
    return valueA - valueB;
  };

/**
 * Generic comparator function for sorting dates
 * Handles null/undefined values by treating them as epoch (Jan 1, 1970)
 *
 * @param key - The key of the property to sort by
 * @returns A comparator function compatible with Array.prototype.sort()
 *
 * @example
 * ```ts
 * const headers = [{
 *   key: 'created_at',
 *   label: 'Created',
 *   sortFn: sortByDate('created_at')
 * }]
 * ```
 */
export const sortByDate =
  (key: string) =>
  (a: TableRow, b: TableRow): number => {
    const valueA = a[key] ? new Date(a[key] as string).getTime() : 0;
    const valueB = b[key] ? new Date(b[key] as string).getTime() : 0;
    return valueA - valueB;
  };

/**
 * Generic comparator function for sorting booleans
 * True values come before false values
 * Handles null/undefined values by treating them as false
 *
 * @param key - The key of the property to sort by
 * @returns A comparator function compatible with Array.prototype.sort()
 *
 * @example
 * ```ts
 * const headers = [{
 *   key: 'is_active',
 *   label: 'Active',
 *   sortFn: sortByBoolean('is_active')
 * }]
 * ```
 */
export const sortByBoolean =
  (key: string) =>
  (a: TableRow, b: TableRow): number => {
    const valueA = Boolean(a[key]);
    const valueB = Boolean(b[key]);
    return valueA === valueB ? 0 : valueA ? -1 : 1;
  };

/**
 * Case-insensitive string comparator
 * Handles null/undefined values by treating them as empty strings
 *
 * @param key - The key of the property to sort by
 * @returns A comparator function compatible with Array.prototype.sort()
 *
 * @example
 * ```ts
 * const headers = [{
 *   key: 'email',
 *   label: 'Email',
 *   sortFn: sortByStringCaseInsensitive('email')
 * }]
 * ```
 */
export const sortByStringCaseInsensitive =
  (key: string) =>
  (a: TableRow, b: TableRow): number => {
    const valueA = ((a[key] as string) || '').toLowerCase();
    const valueB = ((b[key] as string) || '').toLowerCase();
    return valueA.localeCompare(valueB);
  };
